import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart3 } from "lucide-react";
import axios from "axios";
import { useAuth, API } from "@/App";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const NAV_DARK_COLOR = "#0F172A";

// Traffic source labels - must match backend SOURCE_MAP keys exactly
// IMPORTANT: Keep labels consistent with WebsiteTrafficPage.jsx cards
const SOURCE_LABELS = {
  display: "Display",
  direct: "Direct",
  organic: "Organic Search",
  paid: "Paid Search",
  paid_social: "Paid Social",           // Paid social ads (LinkedIn, Facebook, etc.)
  unassigned: "Unassigned",
  paid_video: "Paid Video",
  referral: "Referral",
  email: "Email",
  organic_social: "Organic Social",     // FIXED: Was "Social Media", now matches cards
  ai_referral: "AI Referral",
  other_campaigns: "Other Campaigns",
  offline: "Offline Sources"
};

// Calculate percentage change
const calcDelta = (current, previous) => {
  if (!previous || previous === 0) {
    return current > 0 ? { value: "New", isPositive: true, isNew: true } : { value: "0%", isPositive: null, isNew: false };
  }
  const change = ((current - previous) / previous * 100);
  return {
    value: `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`,
    isPositive: change > 0,
    isNew: false
  };
};

// Delta cell component
const DeltaCell = ({ delta }) => {
  if (delta.isNew) {
    return <span className="text-green-600 font-medium">New</span>;
  }
  if (delta.isPositive === null) {
    return <span className="text-slate-400">-</span>;
  }
  return (
    <span className={`font-medium ${delta.isPositive ? 'text-green-600' : 'text-red-600'}`}>
      {delta.value}
    </span>
  );
};

const TrafficComparisonTable = ({ 
  dateFilter,  // Inherit from parent - { startDate, endDate }
  excludeTurkey = true,
  selectedRegion = "all",
  trafficDataSource = "ga4"  // "ga4" or "hubspot"
}) => {
  const { sessionToken } = useAuth();
  const [comparisonType, setComparisonType] = useState("mom"); // mom = month-over-month, yoy = year-over-year
  const [currentData, setCurrentData] = useState(null);
  const [previousMoMData, setPreviousMoMData] = useState(null);
  const [previousYoYData, setPreviousYoYData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Calculate comparison periods based on parent's date filter
  const { currentLabel, momLabel, yoyLabel, momDates, yoyDates } = useMemo(() => {
    if (!dateFilter?.startDate || !dateFilter?.endDate) {
      const now = new Date();
      const currentMonth = now.toLocaleString('default', { month: 'long' });
      const currentYear = now.getFullYear();
      return {
        currentLabel: `${currentMonth} ${currentYear}`,
        momLabel: `${new Date(now.getFullYear(), now.getMonth() - 1, 1).toLocaleString('default', { month: 'long' })} ${currentYear}`,
        yoyLabel: `${currentMonth} ${currentYear - 1}`,
        momDates: null,
        yoyDates: null
      };
    }

    // Parse dates properly to avoid timezone issues
    const [startYear, startMonth, startDay] = dateFilter.startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = dateFilter.endDate.split('-').map(Number);
    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

    // Current period label - always include year on BOTH dates
    const currentLabel = `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    
    // Previous Period: Previous period of same length - include year on BOTH dates
    const momStart = new Date(start);
    momStart.setDate(momStart.getDate() - daysDiff - 1);
    const momEnd = new Date(start);
    momEnd.setDate(momEnd.getDate() - 1);
    const momLabel = `${momStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${momEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

    // YoY: Same dates last year - include year on BOTH dates
    const yoyStart = new Date(startYear - 1, startMonth - 1, startDay);
    const yoyEnd = new Date(endYear - 1, endMonth - 1, endDay);
    const yoyLabel = `${yoyStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${yoyEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

    // Format dates for API calls (YYYY-MM-DD)
    const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    return {
      currentLabel,
      momLabel,
      yoyLabel,
      momDates: {
        start: formatDate(momStart),
        end: formatDate(momEnd)
      },
      yoyDates: {
        start: formatDate(yoyStart),
        end: formatDate(yoyEnd)
      }
    };
  }, [dateFilter]);

  // Fetch comparison data
  useEffect(() => {
    const fetchComparisonData = async () => {
      if (!sessionToken || !dateFilter?.startDate || !dateFilter?.endDate) return;
      setLoading(true);
      
      try {
        const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
        const baseParams = { 
          Authorization: `Bearer ${sessionToken}`,
          exclude_turkey: excludeTurkey
        };
        if (viewingTenantId) {
          baseParams["X-Tenant-ID"] = viewingTenantId;
        }
        if (selectedRegion && selectedRegion !== "all") {
          baseParams.region = selectedRegion;
        }

        // Determine which API to use
        const endpoint = trafficDataSource === "hubspot" 
          ? `${API}/integrations/analytics/hubspot/traffic-sources`
          : `${API}/integrations/analytics/ga4/traffic-sources`;

        // Fetch current period data (using parent's date filter)
        const currentResponse = await axios.get(endpoint, {
          params: { ...baseParams, start_date: dateFilter.startDate, end_date: dateFilter.endDate }
        });
        setCurrentData(currentResponse.data?.totals || {});

        // Fetch MoM comparison data
        if (momDates) {
          const momResponse = await axios.get(endpoint, {
            params: { ...baseParams, start_date: momDates.start, end_date: momDates.end }
          });
          setPreviousMoMData(momResponse.data?.totals || {});
        }

        // Fetch YoY comparison data
        if (yoyDates) {
          const yoyResponse = await axios.get(endpoint, {
            params: { ...baseParams, start_date: yoyDates.start, end_date: yoyDates.end }
          });
          setPreviousYoYData(yoyResponse.data?.totals || {});
        }

      } catch (error) {
        console.error("Error fetching comparison data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchComparisonData();
  }, [sessionToken, dateFilter, excludeTurkey, selectedRegion, trafficDataSource, momDates, yoyDates]);

  // Build table data
  const buildTableData = (current, previous) => {
    if (!current) return [];
    
    // ALL sources - order matches typical importance
    const sources = ['direct', 'organic', 'paid', 'paid_social', 'organic_social', 'email', 'referral', 'ai_referral', 'display', 'paid_video', 'other_campaigns', 'offline', 'unassigned'];
    
    return sources.map(source => {
      const currentVal = current[source] || 0;
      const previousVal = previous?.[source] || 0;
      const delta = calcDelta(currentVal, previousVal);
      
      return {
        source,
        label: SOURCE_LABELS[source] || source,
        current: currentVal,
        previous: previousVal,
        delta
      };
    }).filter(row => row.current > 0 || row.previous > 0);
  };

  // Build chart data
  const buildChartData = (current, previous) => {
    if (!current) return [];
    
    // All sources for chart
    const sources = ['direct', 'organic', 'paid', 'paid_social', 'organic_social', 'email', 'referral', 'ai_referral', 'display', 'other_campaigns', 'offline'];
    
    return sources
      .map(source => ({
        name: SOURCE_LABELS[source] || source,
        current: current[source] || 0,
        previous: previous?.[source] || 0
      }))
      .filter(item => item.current > 0 || item.previous > 0)
      .sort((a, b) => b.current - a.current);
  };

  const previousData = comparisonType === "mom" ? previousMoMData : previousYoYData;
  const previousLabel = comparisonType === "mom" ? momLabel : yoyLabel;
  const tableData = buildTableData(currentData, previousData);
  const chartData = buildChartData(currentData, previousData);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-[#00A5A8] border-t-transparent rounded-full animate-spin"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!dateFilter?.startDate) {
    return null; // Don't render until date filter is set
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2" style={{ color: NAV_DARK_COLOR }}>
            <BarChart3 className="w-5 h-5" />
            Traffic Sources Comparison
            {excludeTurkey && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded ml-2">
                No Turkey
              </span>
            )}
            <span className={`text-xs px-2 py-0.5 rounded ml-1 ${
              trafficDataSource === "hubspot" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"
            }`}>
              {trafficDataSource === "hubspot" ? "HubSpot" : "GA4"}
            </span>
          </CardTitle>
          <Tabs value={comparisonType} onValueChange={setComparisonType} className="w-auto">
            <TabsList className="h-8">
              <TabsTrigger value="mom" className="text-xs px-3">vs Previous Period</TabsTrigger>
              <TabsTrigger value="yoy" className="text-xs px-3">vs Same Period Last Year</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Comparing <strong>{currentLabel}</strong> vs <strong>{previousLabel}</strong>
          {selectedRegion !== "all" && <span className="ml-2 text-xs bg-slate-100 px-2 py-0.5 rounded uppercase">{selectedRegion}</span>}
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-3 font-semibold bg-slate-50" style={{ color: NAV_DARK_COLOR }}>
                    Traffic Sources
                  </th>
                  <th className="text-right py-3 px-3 font-semibold bg-amber-50" style={{ color: NAV_DARK_COLOR }}>
                    Current Period
                    <div className="text-xs font-normal text-slate-500">Sessions</div>
                  </th>
                  <th className="text-right py-3 px-3 font-semibold bg-slate-50" style={{ color: NAV_DARK_COLOR }}>
                    Previous Period
                    <div className="text-xs font-normal text-slate-500">Sessions</div>
                  </th>
                  <th className="text-right py-3 px-3 font-semibold bg-slate-50" style={{ color: NAV_DARK_COLOR }}>
                    Delta ({comparisonType === "mom" ? "PoP" : "YoY"})
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((row, idx) => (
                    <tr key={row.source} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-slate-100 transition-colors`}>
                      <td className="py-3 px-3 font-medium" style={{ color: NAV_DARK_COLOR }}>
                        {row.label}
                      </td>
                      <td className="text-right py-3 px-3 font-semibold bg-amber-50/50">
                        {row.current.toLocaleString()}
                      </td>
                      <td className="text-right py-3 px-3 text-slate-600">
                        {row.previous.toLocaleString()}
                      </td>
                      <td className="text-right py-3 px-3">
                        <DeltaCell delta={row.delta} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-slate-400">
                      No traffic data available for comparison
                    </td>
                  </tr>
                )}
                {/* Totals row */}
                {tableData.length > 0 && (
                  <tr className="border-t-2 border-slate-300 bg-slate-100 font-bold">
                    <td className="py-3 px-3" style={{ color: NAV_DARK_COLOR }}>Total</td>
                    <td className="text-right py-3 px-3 bg-amber-100">
                      {tableData.reduce((sum, row) => sum + row.current, 0).toLocaleString()}
                    </td>
                    <td className="text-right py-3 px-3">
                      {tableData.reduce((sum, row) => sum + row.previous, 0).toLocaleString()}
                    </td>
                    <td className="text-right py-3 px-3">
                      <DeltaCell delta={calcDelta(
                        tableData.reduce((sum, row) => sum + row.current, 0),
                        tableData.reduce((sum, row) => sum + row.previous, 0)
                      )} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Comparison Chart */}
          <div>
            <h4 className="text-sm font-medium mb-4" style={{ color: NAV_DARK_COLOR }}>
              Sessions by Traffic Source
            </h4>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: NAV_DARK_COLOR }} />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    tick={{ fontSize: 11, fill: NAV_DARK_COLOR }} 
                    width={100}
                  />
                  <Tooltip 
                    contentStyle={{ fontSize: 12, color: NAV_DARK_COLOR }}
                    formatter={(value) => [value.toLocaleString(), '']}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar 
                    dataKey="current" 
                    fill="#3B82F6" 
                    name="Current Period" 
                    radius={[0, 4, 4, 0]}
                  />
                  <Bar 
                    dataKey="previous" 
                    fill="#94A3B8" 
                    name="Previous Period" 
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-slate-400">
                No chart data available
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TrafficComparisonTable;
